import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/dashboard/Sidebar';
import { BookOpen, Video, MessageSquare, TrendingUp, Clock, Target } from 'lucide-react';
import axios from 'axios';

const DashboardHome = () => {
  const [student, setStudent] = useState(null);
  const [progress, setProgress] = useState([]);
  const [nextSession, setNextSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const STUDENT_ID = 'student-demo-1';
  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Fetch student data
      const studentRes = await axios.get(`${API_URL}/api/dashboard/students/${STUDENT_ID}`);
      setStudent(studentRes.data);

      // Fetch progress data
      const progressRes = await axios.get(`${API_URL}/api/dashboard/students/${STUDENT_ID}/progress`);
      setProgress(progressRes.data);

      // Fetch next session
      const sessionRes = await axios.get(`${API_URL}/api/dashboard/students/${STUDENT_ID}/next-session`);
      setNextSession(sessionRes.data);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D47A1] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const currentProgress = progress[0] || {};

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Welcome back, {student?.name}! 👋</h1>
              <p className="text-gray-600 mt-1">Let's continue your learning journey</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#0D47A1] to-[#00BCD4] rounded-full flex items-center justify-center text-white font-bold">
                {student?.name?.charAt(0)}
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="p-6 max-w-7xl mx-auto">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Current Course Card */}
            <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-[#0D47A1]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Current Course</p>
                  <h3 className="text-lg font-bold text-gray-900">AI & Machine Learning</h3>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Progress</span>
                  <span className="text-sm font-bold text-[#0D47A1]">{currentProgress.progress_percentage || 0}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${currentProgress.progress_percentage || 0}%` }}
                  ></div>
                </div>
              </div>

              <button
                onClick={() => window.location.href = '/dashboard/courses'}
                className="w-full bg-gradient-to-r from-[#0D47A1] to-[#00BCD4] text-white font-semibold py-3 rounded-xl hover:shadow-lg transform hover:scale-[1.02] transition-all"
              >
                Continue Learning
              </button>
            </div>

            {/* Next Live Session Card */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-sm p-6 border border-purple-100 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Video className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">Next Live Session</p>
                  <h3 className="text-lg font-bold text-gray-900">{nextSession?.title || 'No upcoming session'}</h3>
                </div>
              </div>
              
              {nextSession && (
                <>
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(nextSession.datetime).toLocaleDateString()} at {new Date(nextSession.datetime).toLocaleTimeString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Target className="w-4 h-4" />
                      <span>{nextSession.duration}</span>
                    </div>
                  </div>

                  <button
                    onClick={() => window.location.href = '/dashboard/sessions'}
                    className="w-full bg-purple-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg transform hover:scale-[1.02] transition-all"
                  >
                    View Details
                  </button>
                </>
              )}
            </div>

            {/* AI Mentor Card */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl shadow-sm p-6 border border-cyan-100 hover:shadow-lg transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-600">AI Mentor</p>
                  <h3 className="text-lg font-bold text-gray-900">Need Help?</h3>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Your AI mentor is available 24/7 to answer questions and provide guidance.
              </p>

              <button
                onClick={() => window.location.href = '/dashboard/mentor'}
                className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-semibold py-3 rounded-xl hover:shadow-lg transform hover:scale-[1.02] transition-all"
              >
                Chat with Mentor
              </button>
            </div>
          </div>

          {/* Learning Stats */}
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Learning Journey</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="w-8 h-8 text-[#0D47A1]" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{student?.enrolled_courses?.length || 0}</div>
                <div className="text-sm text-gray-600">Enrolled Courses</div>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="w-8 h-8 text-green-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{currentProgress.completed_modules?.length || 0}</div>
                <div className="text-sm text-gray-600">Modules Completed</div>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Video className="w-8 h-8 text-purple-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">12</div>
                <div className="text-sm text-gray-600">Live Sessions</div>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Target className="w-8 h-8 text-yellow-600" />
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{student?.completed_courses?.length || 0}</div>
                <div className="text-sm text-gray-600">Certificates</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardHome;